import { decodeDeckString, encodeDeck } from "@izohek/warno-deck-utils";
import type { SimpleDeck } from "@izohek/warno-deck-utils";
import type { Division, Pack, UnitCategory, UnitMap } from "../../types/deck-builder";
import type { Unit } from "../../types/unit";
import { Deck } from "../deck";
import { LookupAdapter } from "../LookupAdapter";

export interface PackChoice {
  unit: string;
  veterancy: number;
  transport: string | null;
}

interface PackConfig {
  pack: string;
  unit: string;
  availableTransports?: string[];
  availableWithoutTransports: boolean;
}

export interface DivisionPhaseState {
  phase: "DIVISION_PICK";
  data: {
    choices: string[];
  };
}

export interface UnitPhaseState {
  phase: "UNIT_PICK";
  data: {
    choices: PackChoice[];
    deckCode: string;
    division: string;
  };
}

export interface CompletePhaseState {
  phase: "COMPLETE";
  data: {
    deckCode: string;
    division: string;
  };
}

export type DeckDraftState = DivisionPhaseState | UnitPhaseState | CompletePhaseState;

export class DeckDraftClientEngine {
  units: Unit[] = [];
  divisions: Division[] = [];
  unitMap: UnitMap = {};
  state?: DeckDraftState;

  /** Memo for {@link deckCanReachFullActivation}; keyed by deck code string. */
  private completionMemo = new Map<string, boolean>();

  /** Stack guard: same deck code during DFS means a no-progress / cyclic transition. */
  private completionVisiting = new Set<string>();

  callbacks: (() => void)[] = [];

  fireCallbacks(): void {
    this.callbacks.forEach((callback) => {
      callback();
    });
  }

  async initializeDraft(units: Unit[], divisions: Division[]): Promise<void> {
    this.units = units;
    this.divisions = divisions;

    const unitMap: UnitMap = {};
    for (const unit of units) {
      unitMap[unit.descriptorName] = unit;
    }
    this.unitMap = unitMap;

    this.completionMemo.clear();
    this.completionVisiting.clear();

    const state = this.generateDivisionPickState(divisions);
    this.state = state;
    this.setStateToLocalStorage(state);
  }

  generateDivisionPickState(divisions: Division[]): DivisionPhaseState {
    const deckDraftStateData: DivisionPhaseState = {
      phase: "DIVISION_PICK",
      data: {
        choices: this.getDivisionChoices(divisions),
      },
    };
    return deckDraftStateData;
  }

  async setStateToLocalStorage(state: DeckDraftState): Promise<void> {
    localStorage.setItem("deckDraftState", JSON.stringify(state));
  }

  async getStateFromLocalStorage(): Promise<DeckDraftState | null> {
    try {
      const state = localStorage.getItem("deckDraftState");
      if (!state) {
        return null;
      }
      return JSON.parse(state) as DeckDraftState;
    } catch (error) {
      console.error("Failed to parse state from localStorage:", error);
      // Clear corrupted state
      localStorage.removeItem("deckDraftState");
      return null;
    }
  }

  clearStateFromLocalStorage(): void {
    localStorage.removeItem("deckDraftState");
  }

  async chooseOption(choice: number): Promise<void> {
    if (this.state?.phase === "DIVISION_PICK") {
      const divisionDescriptor = this.state.data.choices[choice];
      if (!divisionDescriptor) {
        throw new Error("Invalid division choice");
      }
      const deckCode = "";
      const activeDivision = this.divisions.find(
        (division) => division.descriptor === divisionDescriptor
      ) as Division;

      const nextPhaseData: UnitPhaseState = {
        phase: "UNIT_PICK",
        data: {
          choices: this.getUnitChoices(deckCode, activeDivision, this.units, this.divisions),
          division: divisionDescriptor,
          deckCode: deckCode,
        },
      };

      this.state = nextPhaseData;
      this.setStateToLocalStorage(this.state);
      return;
    } else if (this.state?.phase === "UNIT_PICK") {
      const packChoice = this.state.data.choices[choice] as PackChoice;
      const deckCode = this.state.data.deckCode;
      const divisionDescriptor = this.state.data.division;
      const newDeckCode = this.appendPackChoiceToDeckCode(
        deckCode,
        divisionDescriptor,
        packChoice
      );

      const nextSessionStateData: UnitPhaseState = {
        phase: "UNIT_PICK",
        data: {
          division: this.state.data.division,
          choices: this.getUnitChoices(
            newDeckCode,
            this.divisions.find(
              (division) =>
                division.descriptor === (this.state as UnitPhaseState).data.division
            ) as Division,
            this.units,
            this.divisions
          ),
          deckCode: newDeckCode,
        },
      };

      this.state = nextSessionStateData;
      this.setStateToLocalStorage(this.state);
    }

    return;
  }

  /** Encode deck + one card (shared by {@link chooseOption}, DFS, and feasibility simulation). */
  appendPackChoiceToDeckCode(
    deckCode: string,
    divisionDescriptor: string,
    packChoice: PackChoice
  ): string {
    const unitDescriptor = packChoice.unit;
    const transportDescriptor = packChoice.transport;
    const veterancy = packChoice.veterancy;
    const lookupAdapter = new LookupAdapter(this.unitMap, this.divisions);

    let simpleDeck: SimpleDeck;
    if (deckCode === "") {
      simpleDeck = {
        modded: false,
        division: {
          id: this.divisions.find(
            (division: Division) => division.descriptor === divisionDescriptor
          )?.id as number,
        },
        numberCards: 1,
        cards: [
          {
            unit: {
              id: this.getIdForUnit(unitDescriptor, this.units) || 0,
            },
            veterancy: veterancy,
            transport: transportDescriptor
              ? {
                  id: this.getIdForUnit(transportDescriptor, this.units) || 0,
                }
              : undefined,
          },
        ],
      };
    } else {
      simpleDeck = decodeDeckString(deckCode, lookupAdapter);

      simpleDeck.cards.push({
        unit: {
          id: this.getIdForUnit(unitDescriptor, this.units) || 0,
        },
        veterancy: veterancy,
        transport: transportDescriptor
          ? {
              id: this.getIdForUnit(transportDescriptor, this.units) || 0,
            }
          : undefined,
      });

      simpleDeck.numberCards += 1;
    }

    return encodeDeck(simpleDeck);
  }

  getIdForUnit(unitDescriptor: string, units: Unit[]): number | undefined {
    const unit = units.find((unit) => unit.descriptorName === unitDescriptor);
    return unit?.id;
  }

  /**
   * Same eligibility pipeline as non-first {@link getUnitChoices}: categories with affordable next slot,
   * packs in division filtered by availability.
   */
  getEligiblePackConfigsAfterDeck(
    deck: Deck,
    activeDivision: Division,
    allUnits: Unit[]
  ): PackConfig[] {
    const allUnitsMappedByDescriptor: { [key: string]: Unit } = {};

    for (const unit of allUnits) {
      allUnitsMappedByDescriptor[unit.descriptorName] = unit;
    }

    const totalSpentActivationPoints = deck.totalSpentActivationPoints;

    const selectablePackConfigs = this.getSelectablePackConfigsInDivision(activeDivision);

    if (totalSpentActivationPoints === deck.division.maxActivationPoints) {
      return [];
    }

    const categoriesAvailable: UnitCategory[] = [];
    for (const _category in deck.slotCosts) {
      const category = _category as UnitCategory;

      if (
        deck.slotCosts[category].length === 1 &&
        deck.slotCosts[category][0] === 0
      ) {
        continue;
      }

      const nextSlotCost = deck.getNextSlotCostForCategory(category);
      if (nextSlotCost === undefined) {
        continue;
      }

      if (
        nextSlotCost + totalSpentActivationPoints >
        deck.division.maxActivationPoints
      ) {
        continue;
      }

      categoriesAvailable.push(category);
    }

    const packConfigsInAvailableCategories = selectablePackConfigs.filter((packConfig) =>
      categoriesAvailable.includes(
        (allUnitsMappedByDescriptor[packConfig.unit]?.factoryDescriptor as UnitCategory) ||
          ("" as UnitCategory)
      )
    );

    return packConfigsInAvailableCategories.filter((packConfig) => {
      const unitDescriptor = packConfig.unit;
      const availableQuantity = deck.getAvailableQuantityOfUnit(unitDescriptor);
      return availableQuantity >= 1;
    });
  }

  /**
   * Deterministic vet + transport for completion DFS / feasibility simulation (no RNG).
   */
  deterministicRepresentativePackChoice(
    packConfig: PackConfig,
    activeDivision: Division
  ): PackChoice {
    const transport = this.deterministicTransportForSimulation(packConfig);
    const veterancy = this.deterministicVeterancyIndex(packConfig, activeDivision);
    return {
      unit: packConfig.unit,
      transport,
      veterancy,
    };
  }

  private deterministicTransportForSimulation(packConfig: PackConfig): string | null {
    const transports = packConfig.availableTransports;
    if (transports && transports.length > 0) {
      return [...transports].sort()[0] ?? null;
    }
    if (packConfig.availableWithoutTransports) {
      return null;
    }
    return null;
  }

  private deterministicVeterancyIndex(
    packConfig: PackConfig,
    division: Division
  ): number {
    const pack = division.packs.find((p) => p.packDescriptor === packConfig.pack);
    if (!pack) return 0;

    const multipliersWithIndex = pack.numberOfUnitInPackXPMultiplier.map(
      (multiplier, index) => ({ multiplier, index })
    );

    const nonZeroMultipliersWithIndex = multipliersWithIndex.filter(
      (multiplierWithIndex) =>
        multiplierWithIndex.multiplier * pack.numberOfUnitsInPack >= 1
    );

    if (nonZeroMultipliersWithIndex.length === 0) return 0;

    nonZeroMultipliersWithIndex.sort((a, b) => a.index - b.index);
    return nonZeroMultipliersWithIndex[0]!.index;
  }

  /** True if choosing `cfg` (simulated deterministically) can lead to full activation spend. */
  deckHasCompletionAfterSimulatedPick(
    deckCode: string,
    cfg: PackConfig,
    activeDivision: Division
  ): boolean {
    try {
      const choice = this.deterministicRepresentativePackChoice(cfg, activeDivision);
      const nextCode = this.appendPackChoiceToDeckCode(
        deckCode,
        activeDivision.descriptor,
        choice
      );
      return this.deckCanReachFullActivation(nextCode, activeDivision.descriptor);
    } catch {
      return false;
    }
  }

  deckCanReachFullActivation(deckCode: string, divisionDescriptor: string): boolean {
    const memoHit = this.completionMemo.get(deckCode);
    if (memoHit !== undefined) return memoHit;

    if (this.completionVisiting.has(deckCode)) {
      return false;
    }

    this.completionVisiting.add(deckCode);
    try {
      const activeDivision = this.divisions.find((d) => d.descriptor === divisionDescriptor);
      if (!activeDivision) {
        this.completionMemo.set(deckCode, false);
        return false;
      }

      let deck: Deck;
      try {
        deck = Deck.fromDeckCode(deckCode, {
          unitMap: this.unitMap,
          divisions: this.divisions,
        });
      } catch {
        this.completionMemo.set(deckCode, false);
        return false;
      }

      if (deck.totalSpentActivationPoints === deck.division.maxActivationPoints) {
        this.completionMemo.set(deckCode, true);
        return true;
      }

      const eligible = this.getEligiblePackConfigsAfterDeck(
        deck,
        activeDivision,
        this.units
      );

      if (eligible.length === 0) {
        this.completionMemo.set(deckCode, false);
        return false;
      }

      const triedDescriptors = new Set<string>();
      for (const cfg of eligible) {
        if (triedDescriptors.has(cfg.unit)) continue;
        triedDescriptors.add(cfg.unit);

        try {
          const choice = this.deterministicRepresentativePackChoice(cfg, activeDivision);
          const nextCode = this.appendPackChoiceToDeckCode(
            deckCode,
            divisionDescriptor,
            choice
          );
          if (nextCode === deckCode) continue;
          if (this.deckCanReachFullActivation(nextCode, divisionDescriptor)) {
            this.completionMemo.set(deckCode, true);
            return true;
          }
        } catch {
          continue;
        }
      }

      this.completionMemo.set(deckCode, false);
      return false;
    } finally {
      this.completionVisiting.delete(deckCode);
    }
  }

  getUnitChoices(
    deckCode: string,
    activeDivision: Division,
    allUnits: Unit[],
    allDivisions: Division[]
  ): PackChoice[] {
    // deck code can = "" if it's the first pick
    // if deck code is not "" then we need to get the division from the deck code
    // if its the first pick present 3 command units from the division

    const isFirstPick = deckCode === "";

    // if its the first pick, present 3 command units from the division
    if (isFirstPick) {
      const commandUnits = this.getCommandUnitPacksForDivision(activeDivision, allUnits);
      return this.generateUnitChoices(commandUnits, activeDivision);
    } else {
      const deck = Deck.fromDeckCode(deckCode, {
        unitMap: this.unitMap,
        divisions: allDivisions,
      });

      const packConfigsWithAvailability = this.getEligiblePackConfigsAfterDeck(
        deck,
        activeDivision,
        allUnits
      );

      if (packConfigsWithAvailability.length === 0) {
        return [];
      }

      /** Above this, feasibility DFS is too expensive and can freeze the UI on each pick. */
      const FEASIBILITY_MAX_ELIGIBLE_PACKS = 32;

      let poolForChoices = packConfigsWithAvailability;
      if (packConfigsWithAvailability.length <= FEASIBILITY_MAX_ELIGIBLE_PACKS) {
        this.completionMemo.clear();
        this.completionVisiting.clear();
        const feasible = packConfigsWithAvailability.filter((cfg) =>
          this.deckHasCompletionAfterSimulatedPick(deckCode, cfg, activeDivision)
        );
        if (feasible.length > 0) {
          poolForChoices = feasible;
        }
      }

      return this.generateUnitChoices(poolForChoices, activeDivision);
    }
  }

  generateUnitChoices(
    originalUnitsToChooseFrom: PackConfig[],
    activeDivision: Division
  ): PackChoice[] {
    const distinctDescriptors = new Set(
      originalUnitsToChooseFrom.map((config) => config.unit)
    );
    const targetCount = Math.min(3, distinctDescriptors.size);

    const packChoices: PackChoice[] = [];
    let choiceOptions = [...originalUnitsToChooseFrom];

    for (let i = 0; i < targetCount; i++) {
      const unitChoice = this.generateChoice(choiceOptions);
      const transportChoice = this.chooseTransport(unitChoice);
      const veterancyChoice = this.chooseVeterancy(unitChoice, activeDivision);

      packChoices.push({
        unit: unitChoice.unit,
        transport: transportChoice,
        veterancy: veterancyChoice,
      });

      choiceOptions = choiceOptions.filter((choice) => choice.unit !== unitChoice.unit);
    }

    return packChoices;
  }

  chooseTransport(packConfig: PackConfig): string | null {
    let transportChoice: string | null = null;

    let options: (string | null)[] = packConfig.availableTransports || [];

    if (packConfig.availableWithoutTransports) {
      options.push(null);
    }

    if (packConfig?.availableTransports && packConfig?.availableTransports?.length > 0) {
      transportChoice = this.generateChoice(options);
    } else if (packConfig.availableWithoutTransports) {
      transportChoice = null;
    }

    return transportChoice;
  }

  chooseVeterancy(packConfig: PackConfig, division: Division): number {
    const pack: Pack = division.packs.find(
      (pack) => pack.packDescriptor === packConfig.pack
    ) as Pack;
    // choose a multiplier that is not less than equal to 0

    // remove 0s from the veterancy multiplier list and store their indexes

    const multipliersWithIndex = pack.numberOfUnitInPackXPMultiplier.map(
      (multiplier, index) => {
        return {
          multiplier,
          index,
        };
      }
    );

    // remove values that generate less than 1 unit
    const nonZeroMultipliersWithIndex = multipliersWithIndex.filter(
      (multiplierWithIndex) =>
        multiplierWithIndex.multiplier * pack.numberOfUnitsInPack >= 1
    );
    const vetPick = this.generateChoice<{ multiplier: number; index: number }>(
      nonZeroMultipliersWithIndex
    );
    return vetPick.index;
  }

  async completeDraft(): Promise<string> {
    const deckCode = (this.state as UnitPhaseState).data.deckCode;

    const nextSessionStateData: CompletePhaseState = {
      phase: "COMPLETE",
      data: {
        deckCode: deckCode,
        division: (this.state as UnitPhaseState).data.division,
      },
    };

    this.state = nextSessionStateData;
    this.clearStateFromLocalStorage();
    return deckCode;
  }

  /** Abandon current draft and return to division selection (fresh RNG division picks). */
  async restartDraft(): Promise<void> {
    await this.initializeDraft(this.units, this.divisions);
  }

  /** Jump to COMPLETE with the current encoded deck (may be under-filled activation). Requires ≥ 1 card. */
  async finishDraftEarly(): Promise<string> {
    if (this.state?.phase !== "UNIT_PICK") {
      throw new Error("Finish early is only available during unit drafting.");
    }
    if (!this.state.data.deckCode) {
      throw new Error("Add your command unit before finishing.");
    }
    return this.completeDraft();
  }

  getCommandUnitPacksForDivision(division: Division, allUnits: Unit[]): PackConfig[] {
    const packConfigs = this.getSelectablePackConfigsInDivision(division);

    const units = packConfigs
      .map((unitAndPackDescriptor) => {
        return allUnits.find((unit) => unit.descriptorName === unitAndPackDescriptor.unit);
      })
      .filter((unit): unit is Unit => unit !== undefined);

    const commandUnits = units.filter((unit) => unit.isCommand === true);

    const commandUnitAndPackDescriptorsInDivision = packConfigs.filter(
      (unitAndPackDescriptor) => {
        return commandUnits.find((unit) => unit.descriptorName === unitAndPackDescriptor.unit);
      }
    );

    return commandUnitAndPackDescriptorsInDivision;
  }

  getSelectablePackConfigsInDivision(division: Division): PackConfig[] {
    return division.packs.map((pack) => {
      return {
        unit: pack.unitDescriptor,
        pack: pack.packDescriptor,
        availableTransports: pack.availableTransportList,
        availableWithoutTransports: pack.availableWithoutTransport,
      };
    });
  }

  generateChoice<T>(thingsToChooseFrom: T[]): T {
    if (thingsToChooseFrom.length === 0) {
      throw new Error("Cannot generate choice from empty array");
    }
    const randomIndex = Math.floor(Math.random() * thingsToChooseFrom.length);
    const randomThing = thingsToChooseFrom[randomIndex];
    if (randomThing === undefined) {
      throw new Error("Failed to generate choice");
    }
    return randomThing;
  }

  getDivisionChoices(divisions: Division[]): string[] {
    const divisionDescriptors = divisions.map((division) => {
      return division.descriptor;
    });

    return this.generateDivisionChoices(divisionDescriptors);
  }

  generateDivisionChoices(thingsToChooseFrom: string[]): string[] {
    const choices: string[] = [];

    // pick 1 random thing to choose from
    const firstChoice = this.generateChoice(thingsToChooseFrom);
    choices.push(firstChoice);
    // remove choice from things to choose from
    const remainingThingsToChooseFrom = thingsToChooseFrom.filter(
      (thing) => thing !== firstChoice
    );

    if (remainingThingsToChooseFrom.length === 0) {
      return choices;
    }

    // pick 1 random thing to choose from
    const secondChoice = this.generateChoice(remainingThingsToChooseFrom);
    choices.push(secondChoice);
    // remove choice from things to choose from
    const remainingThingsToChooseFrom2 = remainingThingsToChooseFrom.filter(
      (thing) => thing !== secondChoice
    );

    if (remainingThingsToChooseFrom2.length === 0) {
      return choices;
    }
    // pick 1 random thing to choose from
    const thirdChoice = this.generateChoice(remainingThingsToChooseFrom2);
    choices.push(thirdChoice);

    return choices;
  }
}

